import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { getDatabase, getFormat, mcpEndpointUrl, parseData } from '../helpers.js';
import { confirm } from '../prompt.js';

export const endpointsCommand = new Command('endpoints')
  .description('Manage MCP Factory endpoints');

endpointsCommand.command('upsert <slug>')
  .description('Create or update an MCP endpoint')
  .option('--name <name>', 'Endpoint display name (required unless using --config)')
  .option('--description <desc>', 'Endpoint description')
  .option('--tool <spec>', 'Collection tool spec: collection:op1,op2 (repeatable)', collect, [])
  .option('--relationship <spec>', 'Relationship tool spec: rel_type:op1,op2 (repeatable)', collect, [])
  .option('--batch <spec>', 'Batch operation spec: collection_or_rel:op1,op2 (repeatable)', collect, [])
  .option('--sql-query', 'Enable sql_query tool')
  .option('--config <json>', 'Full endpoint config as JSON (per tool: name, names, description_override, filterable_fields, writable_fields — a per-create/update allowlist of settable fields, e.g. [{"field":"status"},{"field":"start_time","param":"when"}], that also generates a typed/described data schema from the collection schema for just those fields, expose_filter, the expose_sort/limit/offset/fields + default_sort/limit/fields machinery controls, the agent_minimal preset, and precondition — a Filter-DSL guard, e.g. {"field":"data.status","op":"eq","value":"free"}, enforced server-side on the tool\'s create/update writes so the generated tool returns 409 if it does not hold and the agent never sees it). Use @file.json to read from file.')
  .option('--mint-token', 'After upsert, also mint an endpoint-bound (consumer) token for this database and print it (handy for sandbox-testing the preview endpoint)')
  .action(async function (this: Command, slug: string, opts: {
    name?: string;
    description?: string;
    tool: string[];
    relationship: string[];
    batch: string[];
    sqlQuery?: boolean;
    config?: string;
    mintToken?: boolean;
  }) {
    const ws = getDatabase(this);
    const client = new ApiClient();

    let body: Record<string, unknown>;

    if (opts.config) {
      // Full JSON config — supports name, names, description_override, filterable_fields per tool.
      // parseData also resolves @file.json (the help text advertised it; plain JSON.parse didn't).
      body = parseData(opts.config);
    } else {
      if (!opts.name) {
        throw new Error('--name is required (or use --config for full JSON config)');
      }
      body = {
        name: opts.name,
        tools: opts.tool.map(parseToolSpec),
      };

      if (opts.description) body.description = opts.description;

      if (opts.relationship.length > 0) {
        body.relationships = opts.relationship.map(parseRelSpec);
      }

      if (opts.batch.length > 0) {
        const operations: Record<string, string[]> = {};
        for (const spec of opts.batch) {
          const parsed = parseSpec(spec);
          operations[parsed.key] = parsed.ops;
        }
        body.batch = { enabled: true, operations };
      }

      if (opts.sqlQuery) body.sql_query = true;
    }

    const data = await client.request('PUT', `/v1/${ws}/endpoints/${slug}`, body);
    const format = getFormat(this);
    console.log(formatOutput(data, format));

    // Convenience: mint a consumer token bound to the endpoint just upserted. The
    // mint + the config write stay two distinct server operations; this only chains
    // the second call CLI-side. The endpoint exists in `ws` now, so the binding resolves.
    if (opts.mintToken) {
      const tokenData = await client.request('POST', '/v1/tokens', {
        name: `${slug} consumer`,
        endpoint_binding: { database_id: ws, endpoint_id: slug },
      });
      if (format === 'json') {
        console.log(formatOutput(tokenData, format));
      } else {
        console.log('\nEndpoint-bound token:');
        console.log(formatOutput(tokenData, format));
        console.log(`MCP URL: ${mcpEndpointUrl(slug)}`);
      }
    }
  });

endpointsCommand.command('get <slug>')
  .description('Get an endpoint')
  .option('--resolved', 'Include resolved collection schemas')
  .action(async function (this: Command, slug: string, opts: { resolved?: boolean }) {
    const ws = getDatabase(this);
    const client = new ApiClient();
    const path = opts.resolved
      ? `/v1/${ws}/endpoints/${slug}/resolved`
      : `/v1/${ws}/endpoints/${slug}`;
    const data = await client.request('GET', path);
    console.log(formatOutput(data, getFormat(this)));
  });

endpointsCommand.command('list')
  .description('List all endpoints')
  .action(async function (this: Command) {
    const ws = getDatabase(this);
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${ws}/endpoints`);
    console.log(formatOutput(data, getFormat(this)));
  });

endpointsCommand.command('delete <slug>')
  .description('Delete an endpoint')
  .option('-y, --yes', 'Skip confirmation prompt')
  .action(async function (this: Command, slug: string, opts: { yes?: boolean }) {
    if (!opts.yes && !(await confirm(`Delete endpoint "${slug}"? This cannot be undone.`))) {
      console.log('Aborted');
      return;
    }
    const ws = getDatabase(this);
    const client = new ApiClient();
    await client.request('DELETE', `/v1/${ws}/endpoints/${slug}`);
    console.log('Deleted');
  });

endpointsCommand.command('url <slug>')
  .description('Get the MCP connection URL for an endpoint')
  .action((slug: string) => {
    console.log(mcpEndpointUrl(slug));
  });

// ---- Helpers ----

function collect(value: string, previous: string[]): string[] {
  return [...previous, value];
}

function parseSpec(spec: string): { key: string; ops: string[] } {
  const [key, opsStr] = spec.split(':');
  if (!key || !opsStr) {
    throw new Error(`Invalid spec '${spec}'. Expected format: name:op1,op2`);
  }
  return { key, ops: opsStr.split(',') };
}

function parseToolSpec(spec: string): Record<string, unknown> {
  const { key, ops } = parseSpec(spec);
  return { collection: key, operations: ops };
}

function parseRelSpec(spec: string): Record<string, unknown> {
  const { key, ops } = parseSpec(spec);
  return { rel_type: key, operations: ops };
}
